import { Button } from "@ui/Button/Button"
import { format } from "date-fns"
import { type FC, useId, useRef, useState } from "react"
import { type DateRange, DayPicker } from "react-day-picker"
import { MdCalendarToday } from "react-icons/md"
import { Flex, styled } from "styled-system/jsx"

import "react-day-picker/style.css"
import "./dayPicker.css"

/** A range with both ends set — what a caller filtering by date actually needs. */
export type DayRange = { from: Date; to: Date }

/**
 * `anchor-scope` confines the single static anchor name to one picker's
 * subtree, so the name never collides when two are mounted on a page.
 */
const Root = styled("div", {
  base: {
    display: "inline-flex",
    anchorScope: "--date-range-picker",
  },
})

const Trigger = styled(Button, {
  base: {
    anchorName: "--date-range-picker",
  },
})

/**
 * The panel is a native popover so it renders in the top layer — a picker
 * inside a dialog is otherwise clipped by the dialog's own bounds.
 */
const Panel = styled("div", {
  base: {
    position: "fixed",
    inset: "auto",
    positionAnchor: "--date-range-picker",
    top: "[anchor(bottom)]",
    left: "[anchor(left)]",
    // Flip above the trigger rather than run off the bottom of the viewport.
    positionTryFallbacks: "[flip-block]",
    marginTop: "xs",
    padding: "sm",
    border: true,
    rounded: true,
    backgroundColor: "surface",
    boxShadow: "light.md",
    width: "[fit-content]",
  },
})

const label = ({ from, to }: DayRange) =>
  `${format(from, "d MMM yyyy")} - ${format(to, "d MMM yyyy")}`

const complete = (range: DateRange | undefined): DayRange | undefined =>
  range?.from && range.to ? { from: range.from, to: range.to } : undefined

type Props = {
  range: DayRange
  onRangeChange: (range: DayRange) => void
}

export const DateRangePicker: FC<Props> = ({ range, onRangeChange }) => {
  const popoverId = useId()
  const popoverRef = useRef<HTMLDivElement>(null)
  // Picking is staged locally: a click on a complete range always leaves `to`
  // set, so committing per click would fire a change — and close the panel —
  // before both ends have been moved. Nothing escapes until Apply.
  const [draft, setDraft] = useState<DateRange | undefined>(range)
  const applied = complete(draft)

  return (
    <Root>
      <Trigger
        icon={MdCalendarToday}
        popoverTarget={popoverId}
      >
        {label(range)}
      </Trigger>
      <Panel
        ref={popoverRef}
        id={popoverId}
        popover="auto"
        onToggle={(event) => {
          // Reopening starts from the applied range, so dismissing the panel
          // discards an unapplied draft.
          if (event.newState === "open") {
            setDraft(range)
          }
        }}
      >
        <DayPicker
          mode="range"
          selected={draft}
          onSelect={setDraft}
        />
        <Flex
          justifyContent="flex-end"
          marginTop="sm"
        >
          <Button
            variant="primary"
            disabled={!applied}
            onClick={() => {
              if (!applied) {
                return
              }
              onRangeChange(applied)
              popoverRef.current?.hidePopover()
            }}
          >
            Apply
          </Button>
        </Flex>
      </Panel>
    </Root>
  )
}
